import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import {
  ArrowLeft,
  Save,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Loader2,
  Pencil,
} from "lucide-react";

export interface OnboardingFormStep {
  id: number;
  label: string;
  // Any lucide-react icon component (typed structurally to stay version-agnostic)
  icon: React.ComponentType<{ className?: string }>;
}

interface OnboardingFormShellProps {
  title: string;
  steps: OnboardingFormStep[];
  currentStep: number;
  onStepChange: (step: number) => void;
  onSaveProgress: () => void;
  onSubmit: () => void;
  saving: boolean;
  loading?: boolean;
  /** Disables the Submit button (e.g. missing signature/agreement) */
  submitDisabled?: boolean;
  /** True once the form has been submitted — hides save/submit actions */
  readOnly?: boolean;
  submittedAt?: string | null;
  /** When provided, the submitted banner offers a button to edit/resubmit */
  onEdit?: () => void;
  /** True when re-submitting a previously submitted form (button label) */
  isResubmission?: boolean;
  /** Portal URL the back buttons navigate to (defaults to /my-onboarding) */
  backTo?: string;
  children: React.ReactNode;
}

/**
 * Shared page chrome for the onboarding pre-start forms: sticky header with
 * Save Progress, step indicator, card layout, and Previous/Next/Submit
 * footer. Mirrors the NewStarterForm layout.
 */
const OnboardingFormShell: React.FC<OnboardingFormShellProps> = ({
  title,
  steps,
  currentStep,
  onStepChange,
  onSaveProgress,
  onSubmit,
  saving,
  loading = false,
  submitDisabled = false,
  readOnly = false,
  submittedAt,
  onEdit,
  isResubmission = false,
  backTo,
  children,
}) => {
  const navigate = useNavigate();
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === steps.length;
  const portalPath = backTo || "/my-onboarding";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-slate-900" />
            <p className="text-sm text-gray-500">Loading your form...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(portalPath)}
                aria-label="Back to Portal"
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>
                  Back<span className="hidden sm:inline"> to Portal</span>
                </span>
              </button>
              <div className="h-6 w-px bg-gray-200 hidden sm:block" />
              <div>
                <h1 className="text-base font-semibold text-gray-900">{title}</h1>
                <p className="text-xs text-gray-500">
                  Step {currentStep} of {steps.length} — {steps[currentStep - 1]?.label}
                </p>
              </div>
            </div>
            {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={onSaveProgress}
                disabled={saving}
                className="gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving..." : "Save Progress"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {readOnly && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-800">
                This form was submitted
                {submittedAt
                  ? ` on ${new Date(submittedAt).toLocaleDateString("en-AU", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}`
                  : ""}
                .
              </p>
            </div>
            {onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
                className="gap-2 border-green-300 text-green-700 hover:bg-green-100"
              >
                <Pencil className="w-4 h-4" />
                Edit Submission
              </Button>
            )}
          </div>
        )}

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 md:gap-4 mb-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            const isClickable = step.id <= currentStep;

            return (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => isClickable && onStepChange(step.id)}
                  disabled={!isClickable}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : isCompleted
                      ? "bg-green-50 text-green-700 border border-green-200"
                      : "bg-gray-100 text-gray-400 border border-gray-200"
                  } ${isClickable ? "cursor-pointer" : "cursor-not-allowed"}`}
                >
                  {isCompleted ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                  <span className="hidden md:inline">{step.label}</span>
                </button>
                {index < steps.length - 1 && (
                  <div
                    className={`hidden md:block w-8 h-px ${
                      step.id < currentStep ? "bg-green-400" : "bg-gray-200"
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
          <div className="p-6 md:p-8">{children}</div>

          {/* Navigation Footer */}
          <div className="px-6 md:px-8 py-5 border-t border-gray-100 flex items-center justify-between">
            <button
              onClick={() => onStepChange(currentStep - 1)}
              disabled={isFirstStep}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isFirstStep
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>

            <span className="text-sm text-gray-400">
              {currentStep} / {steps.length}
            </span>

            {isLastStep ? (
              readOnly ? (
                <button
                  onClick={() => navigate(portalPath)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors"
                >
                  Back to Portal
                </button>
              ) : (
                <Button
                  onClick={onSubmit}
                  disabled={saving || submitDisabled}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-5 py-2.5"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      {isResubmission ? "Resubmit Form" : "Submit Form"}
                      <CheckCircle2 className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )
            ) : (
              <button
                onClick={() => onStepChange(currentStep + 1)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFormShell;
